export const BUSINESS_DAYS_PER_YEAR = 252;

export function daysBetween(startIso: string, end = new Date()) {
  const start = new Date(`${startIso}T12:00:00`);
  const totalDays = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 86_400_000));
  return Math.floor((totalDays * BUSINESS_DAYS_PER_YEAR) / 365);
}

export function calculateFixedIncome(
  principal: number,
  annualCdiRate: number,
  cdiPercentage: number,
  contributionDate: string,
  referenceDate = new Date(),
) {
  const annualRate = (annualCdiRate / 100) * (cdiPercentage / 100);
  const dailyRate = Math.pow(1 + annualRate, 1 / BUSINESS_DAYS_PER_YEAR) - 1;
  const businessDays = daysBetween(contributionDate, referenceDate);
  const updatedValue = principal * Math.pow(1 + dailyRate, businessDays);
  const dailyYield = updatedValue * dailyRate;
  const monthlyYield = updatedValue * (Math.pow(1 + dailyRate, 21) - 1);
  return {
    businessDays,
    dailyRate,
    updatedValue,
    accumulatedYield: updatedValue - principal,
    dailyYield,
    monthlyYield,
    profitability: principal > 0 ? ((updatedValue / principal) - 1) * 100 : 0,
  };
}

export function projectWealth(current: number, monthlyContribution: number, annualRate: number, months: number) {
  const monthlyRate = Math.pow(1 + annualRate / 100, 1 / 12) - 1;
  if (monthlyRate === 0) return current + monthlyContribution * months;
  return current * Math.pow(1 + monthlyRate, months)
    + monthlyContribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate);
}

export function monthsToTarget(current: number, monthlyContribution: number, annualRate: number, target: number) {
  if (current >= target) return 0;
  if (monthlyContribution <= 0 && annualRate <= 0) return null;
  for (let month = 1; month <= 1_200; month += 1) {
    if (projectWealth(current, monthlyContribution, annualRate, month) >= target) return month;
  }
  return null;
}

export function formatHorizon(months: number | null) {
  if (months === null) return "Não atingido nesta projeção";
  if (months === 0) return "Marco já atingido";
  const years = Math.floor(months / 12);
  const remaining = months % 12;
  if (years === 0) return `${remaining} ${remaining === 1 ? "mês" : "meses"}`;
  if (remaining === 0) return `${years} ${years === 1 ? "ano" : "anos"}`;
  return `${years} ${years === 1 ? "ano" : "anos"} e ${remaining} ${remaining === 1 ? "mês" : "meses"}`;
}
export type FixedPosition = { principal: number; cdiPercentage: number; contributionDate: string };
export type HistoryPoint = { date: string; fixed: number; variable: number; total: number };

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

/**
 * Reconstrói a evolução patrimonial ao longo do tempo:
 * - renda fixa é recalculada analiticamente em cada data de referência
 * - renda variável usa o snapshot mais recente disponível até aquela data
 *   (ou o valor atual, quando ainda não há histórico)
 */
export function buildWealthHistory(
  fixedPositions: FixedPosition[],
  annualCdiRate: number,
  currentVariableValue: number,
  snapshots: Array<{ date: string; variable: number }> = [],
  points = 12,
  today = new Date(),
): HistoryPoint[] {
  const todayIso = isoDate(today);
  const startCandidates = [
    ...fixedPositions.map((p) => p.contributionDate),
    ...snapshots.map((s) => s.date),
  ].filter(Boolean).sort();
  const firstIso = startCandidates[0] ?? todayIso;
  const first = new Date(`${firstIso}T12:00:00`);
  const end = new Date(`${todayIso}T12:00:00`);
  const spanDays = Math.max(0, Math.round((end.getTime() - first.getTime()) / 86_400_000));

  const dates: string[] = [];
  if (spanDays <= 0) {
    dates.push(todayIso);
  } else {
    const steps = Math.min(points, Math.max(2, spanDays + 1));
    for (let i = 0; i < steps; i += 1) {
      const offset = Math.round((spanDays * i) / (steps - 1));
      const d = new Date(first.getTime() + offset * 86_400_000);
      const iso = isoDate(d);
      if (dates[dates.length - 1] !== iso) dates.push(iso);
    }
  }

  const sortedSnapshots = [...snapshots].sort((a, b) => a.date.localeCompare(b.date));

  return dates.map((iso) => {
    const ref = new Date(`${iso}T12:00:00`);
    const fixed = fixedPositions.reduce((sum, p) => {
      if (p.contributionDate > iso) return sum;
      return sum + calculateFixedIncome(p.principal, annualCdiRate, p.cdiPercentage, p.contributionDate, ref).updatedValue;
    }, 0);
    const snap = [...sortedSnapshots].reverse().find((s) => s.date <= iso);
    // Sem snapshot anterior: usa o primeiro snapshot disponível (se a data for
    // anterior a ele) ou o valor atual — nunca zera a renda variável.
    const variable = iso === todayIso
      ? currentVariableValue
      : (snap?.variable ?? sortedSnapshots[0]?.variable ?? currentVariableValue);
    return { date: iso, fixed, variable, total: fixed + variable };
  });
}
